<script setup>
/**
 * Your profile: the picture and name colleagues and policyholders see. Both
 * live in SSO, so one save reaches every hub the person works in, and the page
 * names those hubs before anything is changed.
 */
import { computed, onMounted, ref } from 'vue';
import { useRouter } from 'vue-router';
import PageHeader from '../PageHeader.vue';
import PortalDetailsPhotoDialog from '../portal/PortalDetailsPhotoDialog.vue';
import XlAvatar from '../xl/XlAvatar.vue';
import XlButton from '../xl/XlButton.vue';
import XlCard from '../xl/XlCard.vue';
import XlField from '../xl/XlField.vue';
import XlSkeleton from '../xl/XlSkeleton.vue';
import IconImage from '../icons/IconImage.vue';
import IconUserMultiple from '../icons/IconUserMultiple.vue';
import { saveAccountNames, uploadAccountPhoto } from '../../api/portal';
import { roleLabel } from '../../helpers/roles';
import { useMyHubs } from '../../composables/useMyHubs';
import { useAuthStore } from '../../stores/auth';
import { useEventStore } from '../../stores/event';

const auth = useAuthStore();
const eventStore = useEventStore();
const router = useRouter();

const { hubs, failed, load } = useMyHubs();
const hubsKnown = ref(false);
// Everything else on the page is already in hand; only which hubs a change
// reaches is asked for. Its sentence waits behind a placeholder rather than
// changing its words, and its length, in front of the reader (Profile: no loader).
const hubsSettled = ref(false);
onMounted(async () => {
  try {
    await load();
    hubsKnown.value = !failed.value;
  } finally {
    hubsSettled.value = true;
  }
});

const first = ref(auth.userData?.first_name ?? '');
const last = ref(auth.userData?.last_name ?? '');
const errors = ref({});
const saving = ref(false);

const name = computed(() => `${first.value} ${last.value}`.trim());
const hubName = computed(() => eventStore.event.value?.login_event_name || window.store?.subdomain || 'your hub');
const standing = computed(() => `${roleLabel(auth.userRole)} · ${hubName.value}`);
const hubNames = computed(() => hubs.value.map((hub) => hub.name).join(', '));

// Until SSO has said which hubs, the page claims none in particular.
const reach = computed(() => {
  if (!hubsKnown.value) return "Changing them here changes them in every hub you work in and in every policyholder's portal.";
  if (hubs.value.length > 1) {
    return `Changing them here changes them in all ${hubs.value.length} of your hubs — ${hubNames.value} — and in every policyholder's portal.`;
  }
  return "Changing them here changes them in your hub and in every policyholder's portal.";
});

const confirmSaved = () => {
  if (hubs.value.length > 1) {
    window.loadNotification?.({ message: `Saved — updated in all ${hubs.value.length} of your hubs`, info: hubNames.value });
  } else {
    window.loadNotification?.({ message: hubsKnown.value ? 'Saved — updated in your hub' : 'Saved — updated in every hub you work in' });
  }
};

const save = async () => {
  if (saving.value) return;
  saving.value = true;
  errors.value = {};
  try {
    const saved = await saveAccountNames({ first_name: first.value.trim(), last_name: last.value.trim() });
    auth.setUserData({ ...auth.userData, ...saved });
    first.value = saved.first_name;
    last.value = saved.last_name;
    confirmSaved();
  } catch (error) {
    const fields = error?.response?.status === 422 ? error.response.data?.errors ?? {} : {};
    errors.value = { first_name: fields.first_name?.[0] ?? '', last_name: fields.last_name?.[0] ?? '' };
    if (!fields.first_name && !fields.last_name) window.loadNotification?.({ message: "Couldn't save — try again.", error: true });
  } finally {
    saving.value = false;
  }
};

const picker = ref(null);
const cropSrc = ref(null);

// A data: URL, not a blob: one — the cropper reads its source with XHR, and
// every hub's CSP refuses blob: URLs.
const onPicked = (event) => {
  const file = event.target.files?.[0];
  event.target.value = '';
  if (!file) return;

  const reader = new FileReader();
  reader.onload = () => { cropSrc.value = reader.result; };
  reader.readAsDataURL(file);
};

const onPhotoSaved = (url) => {
  cropSrc.value = null;
  auth.setUserData({ ...auth.userData, profile_image: url });
  confirmSaved();
};
</script>

<template>
  <PageHeader />

  <div class="page-content" data-xl-gen="northstar">
    <div class="profile">
      <h1>Your profile</h1>
      <p class="profile__lede">How you appear to your colleagues and to policyholders.</p>

      <XlCard padded class="profile__card">
        <div class="profile__who">
          <XlAvatar :name="name" :src="auth.userData?.profile_image || ''" :size="72" />
          <div class="profile__id">
            <b data-testid="profile-name">{{ name }}</b>
            <div>{{ standing }}</div>
          </div>
          <XlButton variant="ghost" size="sm" @click="picker.click()">
            <template #icon><IconImage color="currentColor" size="15" /></template>
            Change picture
          </XlButton>
        </div>

        <div class="profile__names">
          <XlField v-model="first" label="First name" name="first_name" maxlength="40" :counted="false" :error="errors.first_name" />
          <XlField v-model="last" label="Last name" name="last_name" maxlength="40" :counted="false" :error="errors.last_name" />
        </div>

        <div class="profile__email">
          <XlField
            :model-value="auth.userData?.email ?? ''"
            type="email"
            label="Email address"
            name="email"
            disabled
            hint="This is how you sign in. To change it, go to your XtendLive account."
          />
        </div>

        <p class="profile__reach" data-testid="profile-reach">
          <IconUserMultiple color="currentColor" size="16" />
          <span v-if="!hubsSettled" class="profile__reach-loading" data-testid="profile-reach-loading">
            <XlSkeleton width="min(520px, 92%)" height="0.8em" radius="6px" class="profile__sk" />
          </span>
          <span v-else><b>Your name and picture are the same everywhere.</b> {{ reach }}</span>
        </p>

        <div class="profile__actions">
          <XlButton :loading="saving" data-testid="profile-save" @click="save">Save</XlButton>
          <XlButton variant="ghost" @click="router.push('/overview')">Cancel</XlButton>
        </div>
      </XlCard>
    </div>

    <input ref="picker" type="file" accept="image/jpeg,image/png,image/webp" hidden aria-label="Profile picture" data-testid="picture-input" @change="onPicked" />
    <PortalDetailsPhotoDialog
      v-if="cropSrc"
      :src="cropSrc"
      :upload="uploadAccountPhoto"
      :minimum="200"
      @close="cropSrc = null"
      @saved="onPhotoSaved"
    />
  </div>
</template>

<style scoped>
.profile {
  font-family: var(--xl-font-family);
  color: var(--xl-color-primary-text);
}

.profile__reach-loading { flex: 1; display: flex; align-items: center; min-height: 1.5em; }
.profile__sk { width: auto; }

.profile h1 {
  margin: 0 0 6px;
  font-size: 30px;
  font-weight: var(--xl-font-weight-bold);
  letter-spacing: -0.03em;
}

.profile__lede {
  margin: 0 0 22px;
  font-size: 15px;
  color: var(--xl-color-outline);
}

.profile__card {
  max-width: 620px;
}

.profile__who {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 18px;
}

.profile__id {
  flex: 1;
  min-width: 200px;
  font-size: 13.5px;
  color: var(--xl-color-outline);
}

.profile__id b {
  font-size: 16px;
  color: var(--xl-color-primary-text);
}

/* XlAvatar sets its initials' size inline. */
.profile__who :deep(.xl-avatar) {
  font-size: 22px !important;
}

.profile :deep(.xl-field__label) { font-size: 12.5px; line-height: normal; }
.profile :deep(.xl-field__control) { padding-left: 14px; padding-right: 14px; border-radius: 11px; }
.profile :deep(.xl-field__note) { font-size: 12.5px; }

.profile__names {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 14px;
  margin-top: 18px;
}

.profile__email {
  margin-top: 14px;
}

.profile__reach {
  display: flex;
  align-items: flex-start;
  gap: 9px;
  margin: 18px 0 0;
  padding: 11px 14px;
  border-radius: 12px;
  background: var(--xl-color-primary-container);
  color: var(--xl-color-primary);
  font-size: 13px;
  line-height: 1.55;
}

.profile__reach svg {
  flex: 0 0 auto;
  margin-top: 1px;
}

.profile__actions {
  display: flex;
  gap: 10px;
  margin-top: 18px;
}

@media (max-width: 640px) {
  .profile__names {
    grid-template-columns: 1fr;
  }
}
</style>
